import React, { useCallback, useEffect, useState } from 'react';
import { Card, Form, Button, Table, Row, Col, Alert, InputGroup } from 'react-bootstrap';
import api from '../../../utils/api';

const DRINK_TYPES = ['beer', 'wine', 'spirit', 'mixed_drink', 'other'];

const DEFAULT_PRESETS = [
  { name: 'Beer', type: 'beer', oz: 12, calories: 150 },
  { name: 'Light Beer', type: 'beer', oz: 12, calories: 100 },
  { name: 'Wine', type: 'wine', oz: 5, calories: 125 },
  { name: 'Bourbon (neat)', type: 'spirit', oz: 1.5, calories: 105 },
  { name: 'Old Fashioned', type: 'mixed_drink', oz: 4, calories: 220 },
];

const HealthSettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [syncDays, setSyncDays] = useState(30);
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [newMed, setNewMed] = useState({ name: '', dosage: '' });
  const [newPreset, setNewPreset] = useState({ name: '', type: 'beer', oz: '', calories: '' });

  const load = useCallback(async () => {
    const { data } = await api.get('/health/settings');
    setSettings(data);
    setClientId(data.ouraClientId || '');
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get('oura') === 'connected') setStatus({ type: 'success', msg: 'Oura connected. Run a sync below.' });
    if (params.get('oura') === 'error') setStatus({ type: 'danger', msg: 'Oura connection failed. Check the Client ID/Secret and try again.' });
    if (params.get('oura') === 'denied') setStatus({ type: 'warning', msg: 'Oura authorization was denied.' });
  }, [load]);

  const flash = (type, msg, sticky) => {
    setStatus({ type, msg });
    if (!sticky) setTimeout(() => setStatus(null), 3000);
  };

  const saveSettings = async (updates) => {
    try {
      const { data } = await api.put('/health/settings', updates);
      setSettings(data);
      flash('success', 'Saved.');
    } catch (err) {
      flash('danger', 'Save failed.', true);
    }
  };

  const saveOuraApp = async () => {
    await saveSettings({ ouraClientId: clientId, ouraClientSecret: clientSecret });
    setClientSecret('');
  };

  const connectOura = async () => {
    try {
      const { data } = await api.get('/health/oura/authorize-url');
      window.location.href = data.url;
    } catch (err) {
      flash('danger', err.response?.data?.message || 'Could not start the Oura connection.', true);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/health/oura/sync', { days: syncDays });
      flash('success', `Synced ${data.daysSynced} days and ${data.workoutsSynced} workouts from Oura.`);
    } catch (err) {
      flash('danger', err.response?.data?.message || 'Sync failed.', true);
    }
    setSyncing(false);
  };

  const addMed = () => {
    if (!newMed.name) return;
    saveSettings({ medications: [...settings.medications, { ...newMed, active: true }] });
    setNewMed({ name: '', dosage: '' });
  };

  const toggleMed = (idx) => {
    const medications = settings.medications.map((m, i) => (i === idx ? { ...m, active: !m.active } : m));
    saveSettings({ medications });
  };

  const removeMed = (idx) => {
    saveSettings({ medications: settings.medications.filter((_, i) => i !== idx) });
  };

  const addPreset = () => {
    if (!newPreset.name) return;
    saveSettings({
      drinkPresets: [
        ...settings.drinkPresets,
        { ...newPreset, oz: Number(newPreset.oz) || 0, calories: Number(newPreset.calories) || 0 },
      ],
    });
    setNewPreset({ name: '', type: 'beer', oz: '', calories: '' });
  };

  const removePreset = (idx) => {
    saveSettings({ drinkPresets: settings.drinkPresets.filter((_, i) => i !== idx) });
  };

  if (!settings) return <p>Loading...</p>;

  return (
    <div>
      {status && <Alert variant={status.type} onClose={() => setStatus(null)} dismissible>{status.msg}</Alert>}

      <Card className="mb-3">
        <Card.Header>Oura Ring</Card.Header>
        <Card.Body>
          <p className="text-muted small mb-2">
            Paste the Client ID and Client Secret from your Oura application
            (cloud.ouraring.com), save, then click Connect and approve access.
            {settings.ouraConnected ? ' Status: connected.' : ' Status: not connected yet.'}
            {settings.lastOuraSync && ` Last sync: ${new Date(settings.lastOuraSync).toLocaleString()}.`}
          </p>
          <Row className="g-2 align-items-center">
            <Col md={4}>
              <Form.Control
                placeholder="Client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Form.Control
                type="password"
                placeholder={settings.hasOuraApp ? 'Client Secret (saved — paste to replace)' : 'Client Secret'}
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Button className="me-2" onClick={saveOuraApp} disabled={!clientId.trim()}>Save</Button>
              <Button variant="success" onClick={connectOura} disabled={!settings.hasOuraApp}>
                {settings.ouraConnected ? 'Reconnect' : 'Connect Oura'}
              </Button>
            </Col>
          </Row>
          <Row className="g-2 mt-1">
            <Col md={6}>
              <InputGroup>
                <Form.Select style={{ maxWidth: 160 }} value={syncDays} onChange={(e) => setSyncDays(Number(e.target.value))}>
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last year</option>
                </Form.Select>
                <Button
                  variant="outline-primary"
                  onClick={runSync}
                  disabled={syncing || (!settings.ouraConnected && !settings.hasOuraToken)}
                >
                  {syncing ? 'Syncing...' : 'Sync now'}
                </Button>
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col lg={6}>
          <Card className="mb-3">
            <Card.Header>Medications</Card.Header>
            <Card.Body>
              <Table size="sm" className="mb-2">
                <tbody>
                  {settings.medications.map((m, i) => (
                    <tr key={i} className={m.active ? '' : 'text-muted'}>
                      <td>{m.name}</td>
                      <td>{m.dosage}</td>
                      <td className="text-end">
                        <Button variant="link" size="sm" className="p-0 me-2" onClick={() => toggleMed(i)}>
                          {m.active ? 'pause' : 'resume'}
                        </Button>
                        <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => removeMed(i)}>remove</Button>
                      </td>
                    </tr>
                  ))}
                  {settings.medications.length === 0 && (
                    <tr><td className="text-muted">No medications yet. Add what you take daily.</td></tr>
                  )}
                </tbody>
              </Table>
              <InputGroup size="sm">
                <Form.Control placeholder="Medication" value={newMed.name} onChange={(e) => setNewMed({ ...newMed, name: e.target.value })} />
                <Form.Control placeholder="Dosage" style={{ maxWidth: 120 }} value={newMed.dosage} onChange={(e) => setNewMed({ ...newMed, dosage: e.target.value })} />
                <Button variant="outline-primary" onClick={addMed}>Add</Button>
              </InputGroup>
              <p className="text-muted small mt-2 mb-0">Active meds show up as the daily checklist on the Daily Log tab.</p>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="mb-3">
            <Card.Header>Drink Presets & Weekly Target</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3" style={{ maxWidth: 220 }}>
                <Form.Label>Weekly drink target</Form.Label>
                <Form.Control
                  type="number"
                  value={settings.weeklyDrinkTarget ?? ''}
                  onChange={(e) => setSettings({ ...settings, weeklyDrinkTarget: e.target.value })}
                  onBlur={() => saveSettings({ weeklyDrinkTarget: Number(settings.weeklyDrinkTarget) || 0 })}
                />
              </Form.Group>
              <Table size="sm" className="mb-2">
                <tbody>
                  {settings.drinkPresets.map((p, i) => (
                    <tr key={i}>
                      <td>{p.name}</td>
                      <td className="text-muted">{p.oz} oz</td>
                      <td className="text-muted">{p.calories} cal</td>
                      <td className="text-end">
                        <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => removePreset(i)}>remove</Button>
                      </td>
                    </tr>
                  ))}
                  {settings.drinkPresets.length === 0 && (
                    <tr>
                      <td className="text-muted">
                        No presets.{' '}
                        <Button variant="link" size="sm" className="p-0" onClick={() => saveSettings({ drinkPresets: DEFAULT_PRESETS })}>
                          Load defaults
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <InputGroup size="sm">
                <Form.Control placeholder="Name" value={newPreset.name} onChange={(e) => setNewPreset({ ...newPreset, name: e.target.value })} />
                <Form.Select style={{ maxWidth: 110 }} value={newPreset.type} onChange={(e) => setNewPreset({ ...newPreset, type: e.target.value })}>
                  {DRINK_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                </Form.Select>
                <Form.Control placeholder="oz" type="number" style={{ maxWidth: 70 }} value={newPreset.oz} onChange={(e) => setNewPreset({ ...newPreset, oz: e.target.value })} />
                <Form.Control placeholder="cal" type="number" style={{ maxWidth: 80 }} value={newPreset.calories} onChange={(e) => setNewPreset({ ...newPreset, calories: e.target.value })} />
                <Button variant="outline-primary" onClick={addPreset}>Add</Button>
              </InputGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default HealthSettingsTab;
